/**
 * FlagList — the clause-scoped flags of one clause's review (§13.7's second
 * Verify assertion: "the position renders beside a flagged clause with its
 * basis"). Document-scoped flags (`scope: 'document'`) never reach this
 * component — `ClauseList.tsx` pulls those into its own top-of-tab banner
 * (session 4 FACTS 4: rendering them on clause 1 would be actively
 * misleading).
 *
 * A flag whose `position_id` is `null` still renders (today: `dispute_resolution`) —
 * the positions library holds nothing for that topic yet, and the flag's own
 * `message` says so (G3: the gap stays visible, never hidden).
 */
import { AlertTriangle, AlertCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ClauseFlag, ClauseFlagSeverity } from '../../api/clauses';
import { topicLabel } from './topics';

interface FlagListProps {
  flags: ClauseFlag[];
  clauseId: string;
}

const SEVERITY_PILL: Record<ClauseFlagSeverity, string> = {
  major: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  minor: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  disagreement: 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300',
};

const SEVERITY_ICON: Record<ClauseFlagSeverity, LucideIcon> = {
  major: AlertTriangle,
  minor: AlertCircle,
  disagreement: AlertTriangle,
};

/** Percentages ride the wire as fractions (0.10, never 10) — render as a percent for reading, keep the fraction in `title=`. Never render 0 in place of an absent value (`null` stays `—`). */
function formatFlagValue(value: number | string | null, unit: string | null): { display: string; title?: string } {
  if (value === null || value === undefined) return { display: '—' };
  if (typeof value === 'number') {
    if (unit === 'pct') {
      const pct = value * 100;
      const rounded = Math.round(pct * 10) / 10;
      return { display: `${rounded}%`, title: `fraction: ${value}` };
    }
    return { display: unit ? `${value} ${unit}` : String(value) };
  }
  return { display: value };
}

export function FlagList({ flags, clauseId }: FlagListProps) {
  if (flags.length === 0) return null;

  return (
    <div className="mt-2 space-y-2">
      {flags.map((flag, idx) => {
        const Icon = SEVERITY_ICON[flag.severity];
        const found = formatFlagValue(flag.found_value, flag.found_unit);
        const position = formatFlagValue(flag.position_value, flag.position_unit);
        return (
          <div
            key={idx}
            data-testid={`ctr-flag-${clauseId}-${idx}`}
            className="rounded-lg border border-gray-200 p-2 text-xs dark:border-gray-700"
          >
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-medium ${SEVERITY_PILL[flag.severity]}`}
              >
                <Icon size={12} />
                {flag.severity}
              </span>
              <span className="text-gray-500 dark:text-gray-400">{flag.kind}</span>
              <span
                className="text-gray-400 dark:text-gray-500"
                title={`topic is inferred (§9.3): ${flag.topic}`}
              >
                {topicLabel(flag.topic)}
              </span>
            </div>

            <p className="mt-1 text-gray-700 dark:text-gray-300">{flag.message}</p>

            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 tabular-nums text-gray-600 dark:text-gray-300">
              <span title={found.title}>{`found: ${found.display}`}</span>
              <span title={position.title}>
                {`position: ${position.display}${flag.comparator ? ` (${flag.comparator})` : ''}`}
              </span>
            </div>

            <div data-testid={`ctr-position-${clauseId}-${idx}`} className="mt-1.5 rounded-lg bg-gray-50 p-2 dark:bg-gray-900/40">
              {flag.position_id ? (
                <>
                  {flag.position_text && <p className="text-gray-700 dark:text-gray-300">{flag.position_text}</p>}
                  <p className="mt-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                    basis: {flag.basis ?? '—'}
                  </p>
                </>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">{flag.message}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default FlagList;
